import { useNavigate } from 'react-router-dom'
import { Star, FileText, Trash2 } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { hapticFeedback } from '@tma.js/sdk-react'

export default function FavoritesPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: favorites, isLoading } = useQuery({
    queryKey: ['favorites'],
    queryFn: () => apiClient.getFavorites(),
  })

  const removeFavorite = useMutation({
    mutationFn: (nodeId: string) => apiClient.removeFavorite(nodeId),
    onSuccess: () => {
      if (hapticFeedback.impactOccurred.isAvailable()) {
        hapticFeedback.impactOccurred('light')
      }
      queryClient.invalidateQueries({ queryKey: ['favorites'] })
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-telegram-button/30 border-t-telegram-button" />
          <span className="text-sm text-telegram-hint">Загрузка...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="favorites-page flex flex-col min-h-full">
      {/* Header */}
      <div className="favorites-hero px-5 pt-5 pb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl glass-icon flex items-center justify-center">
            <Star size={18} className="text-yellow-500" />
          </div>
          <div>
            <h1 className="text-base font-bold">Избранное</h1>
            {favorites && favorites.length > 0 && (
              <p className="text-xs text-telegram-hint">{favorites.length} статей</p>
            )}
          </div>
        </div>
      </div>

      {/* List */}
      <div className="favorites-content flex-1 px-3 pb-4">
        {favorites && favorites.length > 0 ? (
          <div className="space-y-2">
            {favorites.map((item, index) => (
              <div
                key={item.id}
                className="favorites-card flex items-center gap-2 p-3 glass-card"
                style={{ animationDelay: `${index * 40}ms` }}
              >
                <button
                  onClick={() => navigate(`/law/${item.law_code}/article/${item.node_id}`)}
                  className="flex-1 flex items-center gap-3 text-left min-w-0 active:opacity-60 transition-opacity relative z-10"
                >
                  <div className="w-8 h-8 rounded-lg glass-icon flex items-center justify-center shrink-0 relative z-10">
                    <FileText size={15} className="text-yellow-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium leading-snug line-clamp-2">{item.title}</div>
                    <span className="text-[10px] font-medium uppercase tracking-wider text-telegram-button">{item.law_code}</span>
                  </div>
                </button>
                <button
                  onClick={() => removeFavorite.mutate(item.node_id)}
                  className="w-8 h-8 flex items-center justify-center rounded-xl glass-icon text-red-400 active:scale-90 transition-all shrink-0 relative z-10"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full glass-icon flex items-center justify-center mb-4">
              <Star size={28} className="text-yellow-400/50" />
            </div>
            <p className="text-sm font-medium mb-1">Пока пусто</p>
            <p className="text-xs text-telegram-hint">Добавляйте статьи в избранное</p>
          </div>
        )}
      </div>
    </div>
  )
}
